import type { PresenceEvent, PresenceView } from '../../collaboration/protocol';
import type { TranslationKey } from '../../i18n/I18n';

/** The list of everyone: people first, then agents, each by account and tab. */
export function rosterOrder(people: readonly PresenceEvent[], isAgent: (userId: string) => boolean): PresenceEvent[] {
  return [...people].sort((a, b) => Number(isAgent(a.userId)) - Number(isAgent(b.userId))
    || a.userId.localeCompare(b.userId) || a.clientId.localeCompare(b.clientId));
}

/** An avatar takes 24 px (36 overlapped by the next 12); your own avatar's group with its chevron 60 (Figma «Toggle»);
 * without room for it, «+N» alone 36 after an 8 px gap. */
const SLOT = 24;
export const OWN_GROUP = 60;
export const COUNT_ONLY = 44;
/** The width the people would take with room enough: their avatars or «+N», then your own group; none with nobody in. */
export const wantedWidth = (people: number, listed: number) => listed ? SLOT * Math.min(3, people) + OWN_GROUP : 0;

/**
 * The header's people (Figma 615:122826): up to three of `people` (agents are only in the list) before your own group,
 * as many as `room` px holds. When they do not all fit, the last slot is «+N» for everyone else of the `listed`.
 * Narrower than your own group, «+N» for everyone (`own` false); null: not even that fits.
 */
export function headerSlots(people: number, listed: number, room: number): { avatars: number; more: number; own: boolean } | null {
  if (room < OWN_GROUP) return room < COUNT_ONLY ? null : { avatars: 0, more: listed, own: false };
  let slots = Math.min(3, people);
  while (slots > 0 && SLOT * slots + OWN_GROUP > room) slots--;
  if (people <= slots) return { avatars: people, more: 0, own: true };
  return slots ? { avatars: slots - 1, more: listed - (slots - 1), own: true } : { avatars: 0, more: 0, own: true };
}

const VIEW_STATUS: Record<PresenceView['kind'], TranslationKey> = {
  floor: '#WebPresenceIn2D', orbit: '#WebPresenceInOrbit', walk: '#WebPresenceInWalk', wall: '#WebPresenceInWalls', ceiling: '#WebPresenceInCeiling',
};
/** A list row's second line where the participant tells no activity: the view they are in. */
export const viewStatus = (person: PresenceEvent): TranslationKey | null => person.presence ? VIEW_STATUS[person.presence.view.kind] : null;
